import React from "react";
import styles from "./Button.module.css";

/**
 * Standardized Button component.
 * @param {Object} props
 * @param {React.ReactNode} [props.svg] - Icon to display
 * @param {'primary' | 'secondary' | 'ghost' | 'danger' | 'success'} [props.variant='primary'] - Button style variant
 * @param {'sm' | 'md' | 'lg'} [props.size='md'] - Button size
 * @param {string} [props.label] - Button text
 * @param {boolean} [props.disabled] - Disabled state
 * @param {string} [props.className] - Extra CSS classes
 * @param {Object} [props.customStyles] - Inline styles
 * @param {Function} [props.onClick] - Click handler
 * @param {'button' | 'submit' | 'reset'} [props.type='button'] - HTML button type
 */
export const Button = ({
  svg,
  variant,
  size,
  label,
  disabled = false,
  className = "",
  customStyles,
  onClick,
  type = "button",
  // Legacy props support (type/size as "btn-primary", "btn-lg" etc.; element unused, consumed to avoid DOM spread)
  element,
  size: legacySize,
  type: legacyType,
  ...rest
}) => {
  const finalVariant = variant || legacyType?.replace("btn-", "") || "primary";
  const finalSize = size || legacySize?.replace("btn-", "") || "md";

  const btnClasses = [
    styles.btn,
    styles[finalVariant] || styles.primary,
    styles[finalSize] || styles.md,
    !label && svg ? styles.iconOnly : "",
    className
  ].filter(Boolean).join(" ");

  const handleClick = (e) => {
    if (!disabled && onClick) {
      onClick(e);
    }
  };

  return (
    <button
      type={type}
      className={btnClasses}
      onClick={handleClick}
      disabled={disabled}
      style={customStyles}
      aria-label={label || rest["aria-label"]}
      data-variant={finalVariant}
      data-size={finalSize}
      {...rest}
    >
      {svg && <span className={styles.btnIcon}>{svg}</span>}
      {label && <span className={styles.btnLabel}>{label}</span>}
    </button>
  );
};

export default Button;
